import { Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import {
  ArrowRight,
  FlaskConical,
  Factory,
  Server,
  Hospital,
  Building2,
  Zap,
  TrainFront,
  type LucideIcon,
} from "lucide-react";
import { type Locale } from "@/i18n/config";
import { pathFor, type PageId } from "@/i18n/slugMap";
import { pageHead } from "@/i18n/head";
import { brancherStrings } from "@/i18n/content/brancher";

// Aligned by index with brancherStrings.industries.
const industryMeta: { id: PageId; icon: LucideIcon }[] = [
  { id: "pharma", icon: FlaskConical },
  { id: "industri", icon: Factory },
  { id: "datacentre", icon: Server },
  { id: "hospitaler", icon: Hospital },
  { id: "kontor", icon: Building2 },
  { id: "energi", icon: Zap },
  { id: "transport", icon: TrainFront },
];

/** <head> for the brancher index: pageHead (canonical/hreflang/breadcrumb) +
 *  keywords and Twitter tags. No Service schema on the listing page. */
export function brancherHead(locale: Locale) {
  const t = brancherStrings[locale];
  const base = pageHead("brancher", locale, t.meta, { breadcrumbName: t.hero.heading });
  return {
    meta: [
      ...base.meta,
      { name: "keywords", content: t.keywords },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: t.ogTitle },
      { name: "twitter:description", content: t.ogDescription },
      { name: "twitter:image", content: t.meta.ogImage ?? "" },
    ],
    links: base.links,
    scripts: base.scripts,
  };
}

export function BrancherIndexPage({ locale }: { locale: Locale }) {
  const t = brancherStrings[locale];

  return (
    <SiteLayout>
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <p className="text-sm uppercase tracking-widest text-foreground/70">{t.hero.eyebrow}</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight">{t.hero.heading}</h1>
          <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.hero.body}</p>
        </div>
      </section>

      <section className="py-24 bg-section-light">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center text-section-light-foreground">{t.list.heading}</h2>
          <p className="mt-6 text-center text-section-light-foreground/80 max-w-3xl mx-auto leading-relaxed">{t.list.intro}</p>
          <div className="mt-12 grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {t.industries.map((industry, i) => {
              const m = industryMeta[i];
              const Icon = m.icon;
              return (
                <article
                  key={industry.title}
                  className="group flex flex-col bg-secondary rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:ring-2 hover:ring-primary/40 hover:-translate-y-1"
                >
                  <div className="relative shrink-0 overflow-hidden bg-background flex items-center justify-center h-44">
                    <Icon className="h-16 w-16 text-primary-foreground transition-transform duration-500 group-hover:scale-110" />
                  </div>
                  <div className="p-5 flex flex-col justify-between flex-1 min-w-0">
                    <div>
                      <h2 className="text-foreground font-bold text-lg mb-2 group-hover:text-primary transition-colors">
                        {industry.title}
                      </h2>
                      <p className="text-foreground/80 text-sm leading-relaxed line-clamp-3">
                        {industry.text}
                      </p>
                    </div>
                    <Link
                      to={pathFor(m.id, locale)}
                      className="mt-4 inline-flex items-center text-foreground/60 text-xs font-semibold uppercase tracking-wider group-hover:text-foreground transition-colors"
                    >
                      {t.list.readMore}
                      <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.cta.heading}</h2>
          <p className="mt-6 text-lg leading-relaxed">{t.cta.body}</p>
          <Link to={pathFor("kontakt", locale)} className="mt-10 btn-cta">
            {t.cta.cta} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
